import {
  ArrowLeft,
  BookOpenText,
  Buildings,
  ArrowRight,
  CaretDown,
  CaretLeft,
  CaretUpDown,
  Check,
  CircleNotch,
  DiscordLogo,
  Copy,
  Cube,
  FileArrowUp,
  FileMd,
  Gear,
  GitBranch,
  GitPullRequest,
  GithubLogo,
  Heartbeat,
  House,
  Info,
  Key,
  MicrosoftTeamsLogo,
  Monitor,
  Moon,
  NotePencil,
  NotionLogo,
  PlugsConnected,
  Plus,
  Pulse,
  Question,
  RocketLaunch,
  Scales,
  ShieldCheck,
  SignOut,
  SlackLogo,
  SlidersHorizontal,
  Sun,
  TerminalWindow,
  Translate,
  Tray,
  TreeStructure,
  UserCircle,
  Users,
  Warning,
  X,
  type Icon,
  type IconProps,
  Kanban,
  ListChecks,
} from "@phosphor-icons/react";
import { cx } from "./cx.js";

/**
 * Icon set: Phosphor Icons (https://phosphoricons.com), regular weight, 16px by default.
 * Every icon is decorative unless an `alt` is given, so screen readers skip it.
 */
export type { IconProps };

const defaults: IconProps = { size: 16, weight: "regular", "aria-hidden": true };

function themed(Component: Icon, overrides: IconProps = {}): (props: IconProps) => React.JSX.Element {
  return function ThemedIcon(props: IconProps) {
    return <Component {...defaults} {...overrides} {...props} />;
  };
}

export const ChevronDownIcon = themed(CaretDown, { weight: "bold" });
export const ChevronLeftIcon = themed(CaretLeft, { weight: "bold" });
export const ChevronUpDownIcon = themed(CaretUpDown, { weight: "bold" });
export const CheckIcon = themed(Check, { weight: "bold" });
export const MarkdownFileIcon = themed(FileMd);
export const PlusIcon = themed(Plus, { weight: "bold" });
export const XIcon = themed(X, { weight: "bold" });
export const CopyIcon = themed(Copy);
export const ArrowRightIcon = themed(ArrowRight, { weight: "bold" });
export const ArrowLeftIcon = themed(ArrowLeft, { weight: "bold" });
// One icon per domain object, so the sidebar distinguishes them at a glance:
// a Change is a pull request, a Component a unit of code, Architecture CI the
// guardrail around them, and Why the question the lineage answers.
export const HomeIcon = themed(House);
export const DecisionIcon = themed(TreeStructure);
export const ChangeIcon = themed(GitPullRequest);
export const ComponentIcon = themed(Cube);
export const GuardrailIcon = themed(ShieldCheck);
export const QuestionIcon = themed(Question);
// Workbench navigation: every item is an action in the workflow, never a
// noun from the data model — Component and Constraint are reached, not browsed.
export const GetStartedIcon = themed(RocketLaunch);
export const InboxIcon = themed(Tray);
export const ReviewIcon = themed(Scales);
export const DraftIcon = themed(NotePencil);
export const HealthIcon = themed(Heartbeat);
export const SettingsIcon = themed(Gear);
export const WorkspaceIcon = themed(Buildings);
export const PreferencesIcon = themed(SlidersHorizontal);
export const ProfileIcon = themed(UserCircle);
export const RepositoryIcon = themed(GitBranch);
export const UsersIcon = themed(Users);
export const KeyIcon = themed(Key);
// A coding agent working in a terminal or editor (D-0021).
export const TerminalIcon = themed(TerminalWindow);
export const SunIcon = themed(Sun);
export const MoonIcon = themed(Moon);
export const MonitorIcon = themed(Monitor);
export const LanguageIcon = themed(Translate);
export const LogOutIcon = themed(SignOut);
export const GitHubIcon = themed(GithubLogo, { weight: "fill" });
// Connectors: the generic plug, and the platform's own mark where Phosphor has one.
export const ConnectorIcon = themed(PlugsConnected);
export const SlackIcon = themed(SlackLogo, { weight: "fill" });
export const NotionIcon = themed(NotionLogo, { weight: "fill" });
export const DiscordIcon = themed(DiscordLogo, { weight: "fill" });
export const TeamsIcon = themed(MicrosoftTeamsLogo, { weight: "fill" });
// Phosphor has no Atlassian or Linear mark: something like the product stands in until the manifest names a logo.
export const ConfluenceIcon = themed(BookOpenText, { weight: "fill" });
export const JiraIcon = themed(Kanban, { weight: "fill" });
export const LinearIcon = themed(ListChecks, { weight: "bold" });
export const UploadIcon = themed(FileArrowUp);
export const AlertIcon = themed(Warning);
export const InfoIcon = themed(Info);
export const ActivityIcon = themed(Pulse);

const Spinner = themed(CircleNotch, { weight: "bold" });
export const SpinnerIcon = (props: IconProps) => <Spinner {...props} className={cx("dt-spin", props.className)} />;
